import React, { useMemo } from "react";
import { CollapsibleSection } from "../components/ui/CollapsibleSection";
import { useLineItemTemplates } from "../hooks/useLineItemTemplates";
import { ScopeTag } from "../shared/common/EstimateEngine/EstimateEngineTypes";
import { MitToReconMapping } from "@rebuild/shared";

interface MitToReconMappingStatusProps {
	mappings: MitToReconMapping[];
}

export const MitToReconMappingStatus: React.FC<MitToReconMappingStatusProps> = ({ mappings }) => {
	const { dbLineItems, isLoading } = useLineItemTemplates();


	const mappedMitLineItemIds = useMemo(() => {
		return mappings.map((m) => m.mitLineItem?.lineItemId).filter(Boolean) as string[];
	}, [mappings]);

	const duplicateMappings = useMemo(() => {
		const counts: Record<string, number> = {};
		for (const id of mappedMitLineItemIds) {
			counts[id] = (counts[id] || 0) + 1;
		}
		const duplicateIds = new Set(Object.keys(counts).filter((id) => counts[id] > 1));
		return dbLineItems.filter((li) => duplicateIds.has(li.id));
	}, [mappedMitLineItemIds, dbLineItems]);

	const totalIssues = duplicateMappings.length;

	if (isLoading) return null;

	return (
		<CollapsibleSection
			title="Mapping Status"
			badge={totalIssues > 0 ? `${totalIssues} issue${totalIssues === 1 ? "" : "s"}` : undefined}
		>

			{duplicateMappings.length > 0 && (
				<div>
					<p className="text-sm font-medium text-gray-700 mb-2">
						Duplicate Mappings ({duplicateMappings.length})
					</p>
					<div className="flex flex-wrap gap-2">
						{duplicateMappings.map((li) => (
							<span key={li.id} className="text-sm px-3 py-1.5 bg-red-50 border border-red-300 rounded-lg text-red-900 font-medium">
								{li.originalLineItem}
							</span>
						))}
					</div>
				</div>
			)}

			{duplicateMappings.length === 0 && (
				<p className="text-sm text-gray-600">There are no duplicates mappings.</p>
			)}
		</CollapsibleSection>
	);
};
